import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon } from "@sagot/ui";
import type { HomeOptionForm } from "./home-model";

const K = "deployKnowledge.widgetStudio.home";

interface HomeOptionRowProps {
  option: HomeOptionForm;
  index: number;
  count: number;
  /** Names of workflows / booking actions by id (the summary line). */
  names: ReadonlyMap<string, string>;
  onEdit: () => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}

/** "Start workflow · Admissions menu", "Open link · khacademy.edu.ph/…" - what a tap does. */
export function useOptionSummary(option: HomeOptionForm, names: ReadonlyMap<string, string>): string {
  const { t } = useTranslation();
  const known = (id: string) => names.get(id) ?? t(`${K}.summary.unavailable`);
  switch (option.action) {
    case "workflow":
      return t(`${K}.summary.workflow`, { target: known(option.workflow_id) });
    case "booking":
      return t(`${K}.summary.booking`, { target: known(option.action_id) });
    case "question":
      return t(`${K}.summary.question`, { target: option.question });
    case "link":
      return t(`${K}.summary.link`, { target: option.url.replace(/^https:\/\//, "") });
    case "person":
      return t(`${K}.summary.person`);
  }
}

export function HomeOptionRow({ option, index, count, names, onEdit, onMove, onRemove }: HomeOptionRowProps) {
  const { t } = useTranslation();
  const summary = useOptionSummary(option, names);
  const label = option.label;
  return (
    <li className="flex items-center gap-3 px-5 py-3" data-testid="home-option">
      <span aria-hidden="true" className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-ink">
        <Icon name={option.icon} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{label}</p>
        <p className="truncate text-xs text-fg-subtle">{summary}</p>
        {option.audience !== "everyone" ? (
          <Badge className="mt-1">{t(`${K}.audience.${option.audience}`)}</Badge>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center">
        <Button variant="ghost" size="sm" iconOnly aria-label={t(`${K}.options.moveUp`, { label })} disabled={index === 0} onClick={() => { onMove(-1); }}>
          <Icon name="chevron-up" />
        </Button>
        <Button variant="ghost" size="sm" iconOnly aria-label={t(`${K}.options.moveDown`, { label })} disabled={index === count - 1} onClick={() => { onMove(1); }}>
          <Icon name="chevron-down" />
        </Button>
        <Button variant="ghost" size="sm" aria-label={t(`${K}.options.edit`, { label })} onClick={onEdit}>
          {t(`${K}.options.editLabel`)}
        </Button>
        <Button variant="ghost" size="sm" iconOnly aria-label={t(`${K}.options.remove`, { label })} onClick={onRemove}>
          <Icon name="trash" />
        </Button>
      </div>
    </li>
  );
}
